import type {
  DataStoryRecipeId,
  DataStoryStepBodyProps,
  RecipePickerValues,
} from "../../../services/dataStories/dataStoryFlow.types";

/** The `{ values, onChange }` contract the flow registry gives every step body. */
export type MethodPickerStepProps = DataStoryStepBodyProps<RecipePickerValues>;

export interface RecipePickerStepProps extends MethodPickerStepProps {
  /**
   * Template cards drawn after the two method cards. Defaults to the full set;
   * pass `[]` for the two methods alone, which then share two wider columns
   * rather than leaving a third empty.
   */
  templates?: TemplateRecipe[];
}

/** One template card in the picker grid. */
export interface TemplateRecipe {
  id: DataStoryRecipeId;
  title: string;
  kind: "system" | "custom" | "imported";
  pickerPreview: "sales" | "executive" | "marketing" | "board" | "financial";
  important?: boolean;
}

/**
 * Default selection. Matches the Figma frame, which opens with the AI method
 * highlighted rather than nothing selected.
 */
export const DEFAULT_RECIPE_ID: DataStoryRecipeId = "ai";

/** The registry's `initialValues` for the recipe step. */
export const initialRecipePickerValues = (): RecipePickerValues => ({
  selectedRecipeId: DEFAULT_RECIPE_ID,
});

/**
 * Template cards shown after the two method cards.
 *
 * Backend TODO: this is placeholder content. Once a templates endpoint exists
 * these become props sourced by the organism, not a module constant.
 */
export const TEMPLATE_RECIPES: TemplateRecipe[] = [
  {
    id: "sales-qbr",
    title: "Sales QBR Deck",
    kind: "imported",
    pickerPreview: "sales",
  },
  {
    id: "executive-qbr",
    title: "Executive QBR report",
    kind: "system",
    pickerPreview: "executive",
  },
  {
    id: "marketing-performance",
    title: "Marketing performance",
    kind: "custom",
    pickerPreview: "marketing",
    important: true,
  },
  {
    id: "board-presentation",
    title: "Board presentation",
    kind: "imported",
    pickerPreview: "board",
  },
  {
    id: "financial-summary",
    title: "Financial summary report",
    kind: "system",
    pickerPreview: "financial",
  },
];

/**
 * Display name of the agent card with this id — Figma 5326:88960's header
 * reads "Agent – <name>".
 *
 * Lives beside `TEMPLATE_RECIPES` because that list is the single source of
 * these names; when it becomes a backend response this resolves against that
 * instead. Falls back to the id so the header is never empty for a recipe the
 * list does not know (an unreleased agent behind a flag, say).
 */
export const agentRecipeTitle = (id: DataStoryRecipeId): string =>
  TEMPLATE_RECIPES.find((recipe) => recipe.id === id)?.title ?? id;
